import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { ArrowDownUp, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Gate } from "@/components/wallet/Gate";
import { SignSheet } from "@/components/wallet/SignSheet";
import { useWallet } from "@/lib/wallet/store";
import { overviewQuery, priceQuery } from "@/lib/wallet/queries";
import { quoteSwapFee } from "@/lib/wallet/fees";
import { formatAmount, truncateAddress } from "@/lib/wallet/format";
import { quoteSwap, xrpToDrops, type AssetRef, type SwapQuote } from "@/lib/wallet/xrpl";
import { assetKey, curatedAssetRef, CURATED_XRPL_ASSETS } from "@/lib/wallet/assets";
import { getBundledAssetLogo } from "@/lib/wallet/asset-logos";
import xrpLogo from "@/assets/tokens/xrp.png";

export const Route = createFileRoute("/swap")({
  staticData: { sitemap: false },
  validateSearch: (search: Record<string, unknown>) => ({
    pay: typeof search["pay"] === "string" ? (search["pay"] as string) : undefined,
    receive: typeof search["receive"] === "string" ? (search["receive"] as string) : undefined,
    amount: typeof search["amount"] === "string" ? (search["amount"] as string) : undefined,
  }),
  head: () => ({
    meta: [
      { title: "DEX Swap — Cobalt Wallet" },
      {
        name: "description",
        content:
          "DEX Swap XRP and issued tokens on the XRP Ledger's native order book with slippage protection and local signing.",
      },
      { property: "og:title", content: "DEX Swap — Cobalt Wallet" },
      {
        property: "og:description",
        content: "On-ledger DEX swaps quoted from live order books.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: () => (
    <Gate>
      <SwapScreen />
    </Gate>
  ),
});

const SLIPPAGE_OPTIONS = [0.005, 0.01, 0.03] as const;

type SwapAssetOption = {
  key: string;
  label: string;
  symbol: string;
  logo: string | null;
  ref: AssetRef;
  balance: number;
  trusted: boolean;
  group: string;
};

function AssetPicker({
  value,
  options,
  groups,
  onChange,
  label,
}: {
  value: string;
  options: SwapAssetOption[];
  groups: { group: string; assets: SwapAssetOption[] }[];
  onChange: (value: string) => void;
  label: string;
}) {
  const selected = options.find((option) => option.key === value);
  return (
    <details className="group relative max-w-[52%] shrink-0">
      <summary
        aria-label={label}
        className="flex h-11 min-w-28 cursor-pointer list-none items-center gap-2 rounded-lg border border-border bg-background px-2.5 outline-none focus:border-accent"
      >
        {selected?.logo ? (
          <span className={`grid size-6 shrink-0 place-items-center overflow-hidden rounded-full ${selected.key === "XRP" ? "bg-background p-1" : "bg-elevated"}`}>
            <img src={selected.logo} alt="" className={`size-full object-contain ${selected.key === "XRP" ? "brightness-0 invert" : ""}`} />
          </span>
        ) : (
          <span className="readout grid size-6 shrink-0 place-items-center rounded-full bg-elevated text-[9px] text-accent">
            {selected?.symbol.slice(0, 2) ?? "?"}
          </span>
        )}
        <span className="readout min-w-0 flex-1 truncate text-[12px]">{selected?.symbol ?? "Select"}</span>
        <span className="text-[10px] text-faint">⌄</span>
      </summary>
      <div className="absolute right-0 z-30 mt-2 max-h-72 w-64 overflow-y-auto rounded-lg border border-border bg-surface p-1.5 shadow-xl">
        {groups.map((entry) => (
          <div key={entry.group} className="mb-1 last:mb-0">
            <p className="label-instrument px-2 py-1.5">{entry.group}</p>
            {entry.assets.map((asset) => (
              <button
                key={asset.key}
                type="button"
                onClick={(event) => {
                  onChange(asset.key);
                  event.currentTarget.closest("details")?.removeAttribute("open");
                }}
                className={`flex w-full items-center gap-2.5 rounded-md p-2 text-left transition-colors hover:bg-elevated ${asset.key === value ? "text-accent" : "text-foreground"}`}
              >
                {asset.logo ? (
                  <span className={`grid size-7 shrink-0 place-items-center overflow-hidden rounded-full ${asset.key === "XRP" ? "bg-background p-1" : "bg-elevated"}`}>
                    <img src={asset.logo} alt="" className={`size-full object-contain ${asset.key === "XRP" ? "brightness-0 invert" : ""}`} />
                  </span>
                ) : (
                  <span className="readout grid size-7 shrink-0 place-items-center rounded-full bg-elevated text-[9px] text-accent">
                    {asset.symbol.slice(0, 2)}
                  </span>
                )}
                <span className="min-w-0">
                  <span className="block text-[12px] font-semibold">{asset.symbol}</span>
                  <span className="readout block truncate text-[9px] text-faint">{asset.label}</span>
                </span>
              </button>
            ))}
          </div>
        ))}
      </div>
    </details>
  );
}

function SwapScreen() {
  const search = Route.useSearch();
  const { address, submit } = useWallet();
  const overview = useQuery(overviewQuery(address));
  const price = useQuery(priceQuery(true));
  const tokens = overview.data?.tokens ?? [];

  const options = useMemo(
    () => {
      const held = tokens.map((t) => ({
        key: `${t.currency}:${t.issuer}`,
        label: `${t.code} · ${truncateAddress(t.issuer)}`,
        symbol: t.code,
        logo: getBundledAssetLogo(t),
        ref: { currency: t.currency, issuer: t.issuer } as AssetRef,
        balance: Number(t.balance),
        trusted: true,
        group: "Your assets",
      }));
      const heldKeys = new Set(held.map((asset) => asset.key));
      return [
        { key: "XRP", label: "XRP", symbol: "XRP", logo: xrpLogo, ref: { currency: "XRP" } as AssetRef, balance: overview.data?.spendable ?? 0, trusted: true, group: "Your assets" },
        ...held,
        ...CURATED_XRPL_ASSETS.filter((asset) => !heldKeys.has(assetKey(curatedAssetRef(asset)))).map((asset) => ({
          key: assetKey(curatedAssetRef(asset)),
          label: `${asset.symbol} · ${asset.name}`,
          symbol: asset.symbol,
          logo: asset.logo,
          ref: curatedAssetRef(asset),
          balance: 0,
          trusted: false,
          group: asset.category === "community" ? "Community assets" : "Curated XRPL assets",
        })),
      ];
    },
    [tokens, overview.data?.spendable],
  );

  const [payKey, setPayKey] = useState(search.pay ?? "XRP");
  const [receiveKey, setReceiveKey] = useState(search.receive ?? "");
  const [amount, setAmount] = useState(search.amount ?? "");
  const [slippage, setSlippage] = useState<number>(0.01);
  const [quote, setQuote] = useState<SwapQuote | null>(null);
  const [quoting, setQuoting] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!receiveKey && options.length > 1) setReceiveKey(options[1]!.key);
  }, [options, receiveKey]);

  const pay = options.find((o) => o.key === payKey);
  const receive = options.find((o) => o.key === receiveKey);
  const numeric = Number(amount);

  useEffect(() => {
    if (!pay || !receive || !numeric || numeric <= 0 || pay.key === receive.key) {
      setQuote(null);
      return;
    }
    let cancelled = false;
    setQuoting(true);
    const timer = window.setTimeout(() => {
      void quoteSwap(pay.ref, receive.ref, numeric)
        .then((q) => {
          if (!cancelled) setQuote(q);
        })
        .catch(() => {
          if (!cancelled) setQuote(null);
        })
        .finally(() => {
          if (!cancelled) setQuoting(false);
        });
    }, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [pay, receive, numeric]);

  const minReceive = quote ? quote.receiveEstimate * (1 - slippage) : 0;
  const overBalance = pay ? numeric > pay.balance : false;
  const canReview = Boolean(quote) && !overBalance && numeric > 0 && pay?.key !== receive?.key;
  const receiveNeedsTrustline = Boolean(receive && receive.ref.currency !== "XRP" && !receive.trusted);
  const canSubmit = canReview && !receiveNeedsTrustline;

  const groupedOptions = ["Your assets", "Curated XRPL assets", "Community assets"].map((group) => ({
    group,
    assets: options.filter((option) => option.group === group),
  })).filter((entry) => entry.assets.length > 0);

  // Fee is charged in XRP on the XRP leg of the trade, at a tier set by the
  // USD notional; token<->token falls back to the flat fee inside quoteSwapFee.
  const xrpLeg =
    pay?.ref.currency === "XRP"
      ? numeric
      : receive?.ref.currency === "XRP" && quote
        ? quote.receiveEstimate
        : null;
  const xrpUsd = price.data ?? null;
  const usdNotional = xrpLeg !== null && xrpUsd ? xrpLeg * xrpUsd : null;
  const serviceFee = useMemo(
    () =>
      quoteSwapFee({
        xrpLeg: Number.isFinite(xrpLeg as number) ? (xrpLeg as number) : null,
        usdNotional,
      }),
    [xrpLeg, usdNotional],
  );


  const amountFor = (asset: AssetRef, value: number) =>
    asset.currency === "XRP"
      ? xrpToDrops(value)
      : { currency: asset.currency, issuer: asset.issuer, value: value.toFixed(6) };

  const executeSwap = async () => {
    if (!pay || !receive || !quote) return;
    setBusy(true);
    try {
      const result = await submit({
        TransactionType: "OfferCreate",
        Account: address ?? "",
        TakerPays: amountFor(receive.ref, minReceive),
        TakerGets: amountFor(pay.ref, numeric),
        Flags: 0x00020000, // tfImmediateOrCancel — never leave a resting offer
      }, { fee: serviceFee });
      if (result.succeeded) {
        toast.success(`Swap executed · ${result.hash.slice(0, 10)}…`);
        if (result.serviceFee?.charged) {
          toast.message(`Service fee ${result.serviceFee.xrp} XRP collected.`);
        } else if (result.serviceFee && !result.serviceFee.charged) {
          toast.message("Swap filled. Service fee could not be collected.");
        }
        setSheetOpen(false);
        setAmount("");
        setQuote(null);
        void overview.refetch();
      } else {
        toast.error(`Not filled: ${result.result}`);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Swap failed.");
    } finally {
      setBusy(false);
    }
  };

  const flip = () => {
    setPayKey(receiveKey);
    setReceiveKey(payKey);
    setAmount("");
    setQuote(null);
  };

  return (
    <div className="animate-rise space-y-4">
      <div>
        <h1 className="text-[18px] font-medium tracking-tight">DEX Swap</h1>
        <p className="readout mt-1 text-[10px] text-faint">
          Quoted from the XRPL order book. Fill-or-cancel — no resting offer is left behind.
        </p>
      </div>

      <section className="panel p-4">
        <p className="label-instrument">You pay</p>
        <div className="mt-1.5 flex gap-2">
          <input
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
            inputMode="decimal"
            placeholder="0.00"
            className="readout h-11 min-w-0 flex-1 rounded-lg border border-border bg-background px-3 text-[15px] outline-none placeholder:text-faint focus:border-accent"
          />
          <AssetPicker
            value={payKey}
            onChange={setPayKey}
            options={options}
            groups={groupedOptions}
            label="Choose asset to pay"
          />
        </div>
        <div className="readout mt-2 flex items-center justify-between text-[10px]">
          <span className="text-faint">AVAILABLE</span>
          <button type="button" onClick={() => setAmount(String(pay?.balance ?? 0))} className="text-accent">
            {formatAmount(pay?.balance ?? 0, true)} · MAX
          </button>
        </div>
      </section>

      <div className="flex justify-center">
        <button
          type="button"
          onClick={flip}
          aria-label="Flip assets"
          className="grid size-9 place-items-center rounded-full border border-border bg-surface text-accent transition-colors hover:bg-surface2"
        >
          <ArrowDownUp className="size-4" />
        </button>
      </div>

      <section className="panel p-4">
        <p className="label-instrument">You receive (estimate)</p>
        <div className="mt-1.5 flex gap-2">
          <div className="readout flex h-11 min-w-0 flex-1 items-center rounded-lg border border-border bg-background px-3 text-[15px]">
            {quoting ? (
              <span className="text-faint">quoting…</span>
            ) : quote ? (
              formatAmount(quote.receiveEstimate, true)
            ) : (
              <span className="text-faint">—</span>
            )}
          </div>
          <AssetPicker
            value={receiveKey}
            onChange={setReceiveKey}
            options={options}
            groups={groupedOptions}
            label="Choose asset to receive"
          />
        </div>

        <div className="readout mt-3 space-y-1.5 text-[10px]">
          <div className="flex justify-between">
            <span className="text-faint">RATE</span>
            <span>{quote ? formatAmount(quote.rate, true) : "—"}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-faint">MIN RECEIVED</span>
            <span className="text-accent">{quote ? formatAmount(minReceive, true) : "—"}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-faint">BOOK DEPTH</span>
            <span>{quote ? formatAmount(quote.depth) : "—"}</span>
          </div>
        </div>

        <div className="mt-3">
          <p className="label-instrument">Max slippage</p>
          <div className="mt-1.5 grid grid-cols-3 gap-2">
            {SLIPPAGE_OPTIONS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSlippage(s)}
                className={`readout h-9 rounded-lg border text-[11px] ${
                  slippage === s
                    ? "border-accent text-accent"
                    : "border-border text-muted hover:bg-elevated"
                }`}
              >
                {(s * 100).toFixed(1)}%
              </button>
            ))}
          </div>
        </div>

        {overBalance ? (
          <p className="readout mt-3 rounded-lg border border-danger/40 bg-danger/10 p-2.5 text-[10px] text-danger">
            Amount exceeds your available balance.
          </p>
        ) : null}
        {!quote && numeric > 0 && !quoting ? (
          <p className="readout mt-3 rounded-lg border border-border p-2.5 text-[10px] text-muted">
            No liquidity found for this pair on the order book.
          </p>
        ) : null}
        {receiveNeedsTrustline ? (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-border bg-background p-3">
            <ShieldAlert className="mt-0.5 size-4 shrink-0 text-accent" />
            <p className="text-[10px] leading-relaxed text-muted">
              Add the {receive?.label} trustline before receiving this asset. Cobalt never creates a trustline silently.
            </p>
          </div>
        ) : null}
      </section>

      <button
        type="button"
        disabled={!canSubmit}
        onClick={() => setSheetOpen(true)}
        className="readout h-12 w-full rounded-lg bg-accent text-[11px] uppercase tracking-[0.15em] text-accent-ink disabled:opacity-40"
      >
        Review swap
      </button>

      <SignSheet
        open={sheetOpen}
        title="Sign swap"
        checksum="✓"
        busy={busy}
        rows={[
          { label: "Operation", value: "OfferCreate · fill or cancel" },
          { label: "Pay", value: `${formatAmount(amount, true)} ${pay?.label ?? ""}` },
          {
            label: "Min receive",
            value: `${formatAmount(minReceive, true)} ${receive?.label ?? ""}`,
            emphasis: "accent",
          },
          { label: "Slippage cap", value: `${(slippage * 100).toFixed(1)}%` },
          {
            label: "Cobalt service fee",
            value: serviceFee.xrp > 0 ? `${serviceFee.xrp} XRP · ${serviceFee.basis}` : "Free",
            emphasis: serviceFee.xrp > 0 ? "accent" : "default",
          },
          { label: "Network", value: "XRPL mainnet" },
        ]}
        warning="Order-book prices move between quote and execution. Anything above your slippage cap is cancelled rather than filled."
        onCancel={() => setSheetOpen(false)}
        onConfirm={() => void executeSwap()}
      />
    </div>
  );
}
